/**
 * 站內即時提醒的純規則（2026-08-27）— 前端輪詢後決定「哪幾筆要彈出提示」。
 * 抽成純函式便於測試：不碰 React、不碰網路。
 */

export interface FeedItem {
  id: number;
  title: string;
  body: string;
  viewedAt: number | null;
  status: "pending" | "sent" | "failed" | "viewed";
  createdAt: Date | string | number;
  payload?: unknown;
  failureReason?: string | null;
}

/**
 * 使用者沒啟用瀏覽器推播時，每則通知的 push 通道都會被標記失敗。
 * 這不是「系統出問題」，站內通知與通知中心都正常 —— 不該對使用者顯示紅色失敗標記。
 * 對照 server/services/notification.service.ts 的 markFailed 呼叫。
 */
export const NO_SUBSCRIPTION_REASON = "no active subscription";

/** 是否為需要讓使用者知道的真正發送失敗 */
export function isDeliveryFailure(n: Pick<FeedItem, "status" | "failureReason">): boolean {
  if (n.status !== "failed") return false;
  return (n.failureReason ?? "").trim() !== NO_SUBSCRIPTION_REASON;
}

/** 一筆通知是否未讀（與通知中心頁同一套判定） */
export function isUnread(n: Pick<FeedItem, "viewedAt" | "status">): boolean {
  return !n.viewedAt && n.status !== "viewed";
}

/** 從 payload 取出站內路徑（去掉網域，避免導到外部網址） */
export function toInternalPath(payload: unknown): string | null {
  const url = (payload as { url?: unknown } | null | undefined)?.url;
  if (typeof url !== "string" || url === "") return null;
  const path = url.replace(/^https?:\/\/[^/]+/, "");
  return path.startsWith("/") ? path : null;
}

/** 同時最多彈出幾則，避免一次湧入洗版 */
export const MAX_TOASTS = 3;

export interface ToastSelection<T extends FeedItem> {
  /** 這一輪要彈出的通知（舊到新） */
  toasts: T[];
  /** 更新後的水位；下一輪用它比對 */
  nextSeenId: number;
  /** 這一輪有幾則因為上限而未彈出 */
  suppressed: number;
}

/**
 * 依「已看過的最大 id」挑出新通知。
 * - lastSeenId 為 null＝首次載入：只設定水位、不彈出（避免登入瞬間洗版）
 * - 只彈未讀的；已在通知中心讀過的不再打擾
 * - 超過 MAX_TOASTS 時取最新幾則，其餘計入 suppressed
 */
export function selectToasts<T extends FeedItem>(list: T[], lastSeenId: number | null): ToastSelection<T> {
  const maxId = list.reduce((m, n) => Math.max(m, n.id), 0);
  if (lastSeenId === null) return { toasts: [], nextSeenId: maxId, suppressed: 0 };

  const fresh = list
    .filter((n) => n.id > lastSeenId && isUnread(n))
    .sort((a, b) => a.id - b.id);

  const toasts = fresh.slice(-MAX_TOASTS);
  return {
    toasts,
    nextSeenId: Math.max(lastSeenId, maxId),
    suppressed: fresh.length - toasts.length,
  };
}
